import { IReferral } from '@sales-center/common'
import { useCallback, useEffect } from 'react'
import { ISource } from '@user/store/sources'
import { ICampaign } from '@user/store/campaigns'
import { useFormikContext } from 'formik'
import { ILeadFormModel } from '../../typings'

type Props = {
  activeLeadSources: ISource[]
  activeLeadCampaigns: ICampaign[]
}

export function useHandlers(props: Props) {
  const { activeLeadCampaigns, activeLeadSources } = props

  const {
    values: {
      locationSameAsBillTo,
      divisionId,
      division,
      emailAddresses,
      phoneNumber,
      phoneExt,
      altPhoneNumber,
      altPhoneExt,
      locationTypeId,
      locationType,
      address1,
      address2,
      city,
      state,
      postalCode,
      firstName,
      lastName,
      company,
      title,
      mobilePhone,
      county,
      externalLinkId,
      externalLinkType,
      contactTime,
      contactMethod,
      externalLocationId,
      lat,
      lng,
    },
    setFieldValue,
    setValues,
  } = useFormikContext<ILeadFormModel>()

  const onChangeReferral = useCallback(
    (referral: IReferral) => {
      setFieldValue('referral', referral)
    },
    [setFieldValue],
  )

  const onChangeSource = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const source = activeLeadSources.find((x) => x.id === e.target.value)
      setFieldValue('leadSourceId', source?.id)
      setFieldValue('leadSource', source?.name)
    },
    [setFieldValue, activeLeadSources],
  )

  const onChangeCampaign = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const cItem = activeLeadCampaigns.find((x) => x.id === e.target.value)
      const sourceItem = activeLeadSources.find((x) => x.name === cItem?.source)
      if (cItem) {
        setValues((v) => ({
          ...v,
          leadSource: sourceItem ? sourceItem.name : null,
          leadSourceId: sourceItem ? sourceItem.id : '',
          campaign: cItem.name,
          campaignId: cItem.id,
        }))
      }
    },
    [setValues, activeLeadCampaigns, activeLeadSources],
  )

  useEffect(() => {
    if (locationSameAsBillTo)
      setValues((prevValues) => ({
        ...prevValues,
        billTo_divisionId: divisionId,
        billTo_division: division,
        billTo_emailAddresses: emailAddresses,
        billTo_phoneNumber: phoneNumber,
        billTo_phoneExt: phoneExt,
        billTo_altPhoneNumber: altPhoneNumber,
        billTo_altPhoneExt: altPhoneExt,
        billTo_locationTypeId: locationTypeId,
        billTo_locationType: locationType,
        billTo_address1: address1,
        billTo_address2: address2,
        billTo_city: city,
        billTo_state: state,
        billTo_postalCode: postalCode,
        billTo_firstName: firstName,
        billTo_lastName: lastName,
        billTo_company: company,
        billTo_title: title,
        billTo_mobilePhone: mobilePhone,
        billTo_county: county,
        billTo_latitude: lat,
        billTo_longitude: lng,
        billTo_externalLinkId: externalLinkId,
        billTo_externalLinkType: externalLinkType,
        billTo_contactTime: contactTime,
        billTo_contactMethod: contactMethod,
      }))
  }, [
    setValues,
    locationSameAsBillTo,
    divisionId,
    division,
    phoneNumber,
    phoneExt,
    altPhoneNumber,
    altPhoneExt,
    locationTypeId,
    locationType,
    address1,
    address2,
    city,
    state,
    postalCode,
    firstName,
    lastName,
    company,
    title,
    mobilePhone,
    county,
    externalLinkId,
    externalLinkType,
    contactTime,
    contactMethod,
    lat,
    lng,
  ])

  useEffect(() => {
    if (!locationSameAsBillTo && !externalLocationId)
      setValues((prevValues) => ({
        ...prevValues,
        billTo_division: '',
        billTo_divisionId: '',
        billTo_locationTypeId: '',
        billTo_locationType: '',

        billTo_firstName: '',
        billTo_lastName: '',
        billTo_title: '',
        billTo_company: '',
        billTo_emailAddresses: [],
        billTo_phoneNumber: '',
        billTo_phoneExt: '',
        billTo_mobilePhone: '',
        billTo_altPhoneNumber: '',
        billTo_altPhoneExt: '',
        billTo_address1: '',
        billTo_address2: '',
        billTo_city: '',
        billTo_state: '',
        billTo_postalCode: '',
        billTo_county: '',
        billTo_latitude: 0,
        billTo_longitude: 0,
        billTo_externalLinkId: '',
        billTo_externalLinkType: '',
        billTo_contactTime: '',
        billTo_contactMethod: '',
        billTo_targetPests: [],
      }))
  }, [setValues, locationSameAsBillTo, externalLocationId])

  return { onChangeCampaign, onChangeReferral, onChangeSource }
}
